// MicButton: pulsante push-to-talk con stati visivi differenti.
//
// UX walkie-talkie: tieni premuto per parlare, rilascia per inviare.
// Funziona sia con mouse (mousedown/up/leave) sia con touch (touchstart/end).

import { useTranslation } from 'react-i18next';
import type { PushToTalkState } from '../lib/usePushToTalk';

interface MicButtonProps {
  state: PushToTalkState;
  recordingMs: number;
  error: string | null;
  onStart: () => void;
  onStop: () => void;
  onReset: () => void;
  disabled?: boolean;
}

const SIZE_PX = 96;

export function MicButton({
  state,
  recordingMs,
  error,
  onStart,
  onStop,
  onReset,
  disabled,
}: MicButtonProps) {
  const { t } = useTranslation();

  const recordingSec = Math.floor(recordingMs / 1000);

  // Colori e icona in base allo stato
  const bg =
    state === 'recording'
      ? 'var(--danger)'
      : state === 'processing'
        ? 'var(--accent)'
        : state === 'denied' || state === 'unsupported' || state === 'error'
          ? 'var(--mute)'
          : 'var(--primary)';

  const label =
    state === 'recording'
      ? t('chat.voiceRecording', { defaultValue: 'Sto ascoltando...' })
      : state === 'processing'
        ? t('chat.voiceProcessing', { defaultValue: 'Sto trascrivendo...' })
        : state === 'requesting'
          ? t('chat.voiceRequestingMic', { defaultValue: 'Permesso microfono...' })
          : state === 'denied'
            ? t('chat.voiceDenied', { defaultValue: 'Microfono negato' })
            : state === 'unsupported'
              ? t('chat.voiceUnsupported', { defaultValue: 'Browser non supportato' })
              : state === 'error'
                ? t('chat.voiceError', { defaultValue: 'Errore microfono' })
                : t('chat.voicePressAndHold', { defaultValue: 'Premi e parla' });

  const isHoldable = state === 'idle' && !disabled;

  const handlers = isHoldable
    ? {
        onMouseDown: (e: React.MouseEvent) => {
          e.preventDefault();
          onStart();
        },
        onMouseUp: () => onStop(),
        onMouseLeave: () => onStop(),
        onTouchStart: (e: React.TouchEvent) => {
          e.preventDefault();
          onStart();
        },
        onTouchEnd: (e: React.TouchEvent) => {
          e.preventDefault();
          onStop();
        },
        onTouchCancel: () => onStop(),
      }
    : state === 'recording'
      ? {
          // Durante recording: rilascio mouse/touch ferma
          onMouseUp: () => onStop(),
          onMouseLeave: () => onStop(),
          onTouchEnd: () => onStop(),
          onTouchCancel: () => onStop(),
        }
      : {};

  const isClickToReset = state === 'denied' || state === 'error';

  return (
    <div className="flex flex-col items-center gap-3" style={{ minHeight: 160 }}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={state === 'recording'}
        disabled={
          state === 'requesting' || state === 'processing' || state === 'unsupported' || disabled
        }
        onClick={isClickToReset ? () => onReset() : undefined}
        className="relative flex select-none items-center justify-center rounded-full text-white transition-all"
        style={{
          width: SIZE_PX,
          height: SIZE_PX,
          background: bg,
          boxShadow:
            state === 'recording'
              ? '0 0 0 8px rgba(201, 74, 46, 0.3), 0 0 0 16px rgba(201, 74, 46, 0.15)'
              : '0 4px 12px rgba(42, 132, 120, 0.25)',
          transform: state === 'recording' ? 'scale(1.05)' : 'scale(1)',
          touchAction: 'manipulation',
        }}
        {...handlers}
      >
        <MicIcon size={36} />
        {state === 'recording' ? (
          <span
            className="absolute inset-0 rounded-full animate-ping"
            style={{ background: 'var(--danger)', opacity: 0.4 }}
          />
        ) : null}
      </button>

      <div className="text-center">
        <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
          {label}
          {state === 'recording' ? ` · ${recordingSec}s` : null}
        </div>
        {error && state === 'error' ? (
          <div className="mt-1 text-xs" style={{ color: 'var(--danger)' }}>
            {error === 'NO_MICROPHONE'
              ? t('chat.voiceNoMicError', { defaultValue: 'Nessun microfono trovato' })
              : error}
          </div>
        ) : null}
        {state === 'denied' ? (
          <div className="mt-1 text-xs" style={{ color: 'var(--danger)' }}>
            {t('chat.voiceDeniedHelp', {
              defaultValue:
                'Vai nelle impostazioni del browser e concedi i permessi al microfono per continuare.',
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MicIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="currentColor"
      />
      <path
        d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
